'use client';
import { useEffect, useState } from 'react';
import { OnProgressProps } from 'react-player/base';
import { AnimatePresence } from 'motion/react';
import { Box } from '@osmosis/tailwind-ui/components/Box';

import { LearnDataVideo } from '@osmosis/shared/types/learn';
import { VideoSourceData } from '@osmosis/shared/types/learndb/learnData';

import { Video } from './Video';
import VideoEndOverlay from './VideoEndOverlay';
import VideoPauseOverlay from './VideoPauseOverlay';
import TopVideoControls from './TopVideoControls';

import { useUserContext } from '../../context/UserContext';
import { useVideoPlayer } from '../../context/VideoPlayerProvider';
import useModal from '../../hooks/useModal';
import useVideoSrc from '../../hooks/useVideoSrc';
import { track } from '../../lib/util/tracking';
import { useLearnAccess } from '../../context/LearnAccessContext';
import BlurredBlockAuth from '../learn/blocks/BlurredBlockAuth';
import VideoPoster from './VideoPoster';
import { FormattedMessage } from 'react-intl';
import { useVideoThumbnail } from '@osmosis/nextjs-components/hooks/useVideoThumbnail';
import { MAX_LOGGED_OUT_WATCH_SECONDS } from '../../constants/lockout';

export type Props = {
  video: VideoSourceData;
  mediaID: LearnDataVideo['mediaID'];
  learnNodeID: LearnDataVideo['learnNodeID'];
  name: LearnDataVideo['name'];
  isPrimeOnly: LearnDataVideo['isPrimeOnly'];
  upNext: LearnDataVideo['upNext'];
  breadcrumbsv2: LearnDataVideo['breadcrumbsv2'];
  flashcards: LearnDataVideo['sections']['flashcards'];
  questions: LearnDataVideo['sections']['questions'];
  tags: LearnDataVideo['tags'];
  autoplay?: boolean;
  isEmbed?: boolean;
  /** Enables the pause prompts overlay (Learn page only). */
  showPausePrompts?: boolean;
  showOAIButton?: boolean;
  classNames?: {
    controlBar?: string;
  };
};

const TOP_VIDEO_CONTROLS_CLASSNAME = 'top-video-controls';
const TIME_REMAINING_OVERLAY = 10;

/**
 * VideoContainer element.
 */
export const VideoContainer = (props: Props) => {
  const {
    video,
    mediaID,
    learnNodeID,
    name,
    isPrimeOnly,
    breadcrumbsv2,
    tags,
    autoplay,
    isEmbed,
    showPausePrompts,
    showOAIButton,
  } = props;

  const { chapters, captions } = video;

  const poster = useVideoThumbnail(video);

  const { user } = useUserContext();
  const [hasShownOverlay, setHasShownOverlay] = useState(false);
  const [isLockedOut, setIsLockedOut] = useState(false);

  // Get src data
  const srcData = useVideoSrc(video);
  const videoSrcData = (!isLockedOut && srcData) || undefined;

  const {
    playing,
    getPlayer,
    handleSeekTo,
    handlePlay,
    handlePause,
    setHotkeysEnabled,
    isFullscreen,
    handleFullscreen,
  } = useVideoPlayer();
  const endOverlay = useModal();

  const { handleVideoAccess, denyReason } = useLearnAccess();
  const expiredUser = denyReason === 'expired';

  const checkAccess = (type: string) => {
    handleVideoAccess(null, () => {
      handlePause();
      setIsLockedOut(true);

      if (isFullscreen) handleFullscreen();

      track('show_learn_page_lockout', { type }, ['keen']);
    });
  };
  const handleProgress = (state: OnProgressProps) => {
    // Check if should show next up
    if (
      !hasShownOverlay &&
      playing &&
      (getPlayer()?.getDuration() || 0) - state.playedSeconds <
        TIME_REMAINING_OVERLAY
    ) {
      endOverlay.show();
      setHasShownOverlay(true);
    }

    // Check if should show lockout
    if (playing && state.playedSeconds >= MAX_LOGGED_OUT_WATCH_SECONDS) {
      checkAccess('Timed video lockout');
    }
  };

  const handleReplay = () => {
    handleSeekTo(0);
    setHasShownOverlay(false);
    endOverlay.hide();
    handlePlay();
  };

  useEffect(() => {
    if (endOverlay.isVisible) {
      setHotkeysEnabled(false);
    } else {
      setHotkeysEnabled(true);
    }
  }, [endOverlay.isVisible]);

  useEffect(() => {
    // Reset the overlay state whenever the video changes
    setHasShownOverlay(false);
  }, [mediaID]);

  const renderVideoContent = () => {
    if (!isLockedOut) {
      return (
        <Video
          autoplay={autoplay}
          srcData={videoSrcData}
          mediaNodeID={mediaID}
          poster={poster}
          chapters={chapters}
          captions={captions}
          onProgress={handleProgress}
          mediaID={mediaID}
          learnNodeID={learnNodeID}
          name={name}
          isPrimeOnly={isPrimeOnly}
          breadcrumbs={breadcrumbsv2}
          isEmbed={isEmbed}
          onEnded={() => {
            checkAccess('Ended video lockout');
          }}
          classNames={props.classNames}
          overlayComponents={[
            <TopVideoControls
              key="top-video-controls"
              video={{
                learnNodeID,
                name,
              }}
              isEmbed={isEmbed}
              containerProps={{
                className: `${TOP_VIDEO_CONTROLS_CLASSNAME} opacity-0 group-hover:opacity-100 group-[&:focus-within]:opacity-100`,
              }}
              toBeRetiredTag={tags?.find(
                ({ tag }) => tag.toLowerCase() === 'to be retired',
              )}
            />,
          ]}
          pauseOverlay={
            !endOverlay.isVisible && showPausePrompts ? (
              <VideoPauseOverlay />
            ) : null
          }
          showOAIButton={showOAIButton}
        />
      );
    }

    return (
      <Box className="relative h-[665px]">
        <VideoPoster poster={poster} />
        <BlurredBlockAuth
          text={
            expiredUser ? (
              <FormattedMessage
                id="answers.lockout-modal.expired"
                defaultMessage="You're currently on a free account. Upgrade to continue watching and unlock all learning content."
              />
            ) : (
              <FormattedMessage
                id="answers.lockout-modal.heading"
                defaultMessage="Start learning with a free Osmosis account."
              />
            )
          }
          fullHeight={true}
          expiredUser={expiredUser}
        />
      </Box>
    );
  };

  return (
    <Box className="group relative h-full">
      {user && (
        <AnimatePresence>
          {endOverlay.isVisible && (
            <VideoEndOverlay onHide={endOverlay.hide} onReplay={handleReplay} />
          )}
        </AnimatePresence>
      )}
      {renderVideoContent()}
    </Box>
  );
};
